import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FileType } from '../../../../types';
import { updateTitle } from '../../selectedVideoSlice';
import { RootState } from '../../../../reducers';

interface RenameFileModalProps {
  onClose: () => void;
}

const RenameFileModal: React.FC<RenameFileModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { globalModals } = useSelector((state: RootState) => state);
  console.log('Debug globalModals:', {
    globalModals,
    actionData: globalModals.actionData,
  });
  const { files = [], status } = globalModals.actionData || {};
  const file = files[0] as FileType;
  const { _id: fileId, name: currentName } = file || {};
  const [newName, setNewName] = useState(currentName);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState('');
  const isLoading = status === 'pending';

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setNewName(value);
    },
    [],
  );

  const handleConfirm = () => {
    if (isLoading) return;
    dispatch(updateTitle({ fileId, title: newName.trim() }));
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading && newName.trim()) {
      handleConfirm();
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('selectedVideo.actions.modals.renameFile.title', {
              fileName: currentName,
            })}
          </DialogTitle>
          <DialogDescription>
            {t('selectedVideo.actions.modals.renameFile.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="new-file-name">
              {t('selectedVideo.actions.modals.renameFile.label')}
            </Label>
            <Input
              id="new-file-name"
              value={newName || ''}
              onChange={handleNameChange}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={Boolean(error) || !newName?.trim() || isLoading}
          >
            {isLoading ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                {t('selectedVideo.actions.modals.renameFile.renaming')}
              </>
            ) : (
              t('common.rename')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RenameFileModal;
